import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import PromptDialogHeader from "@entrilia/shared/components/dialog/PromptDialogHeader.tsx";
import SearchField from "@entrilia/shared/components/inputs/SearchField.tsx";
import type { FundType, ReportTemplate, ReportTemplateDto } from "../../../../../api/types/svTypes.ts";
import CompanyTemplatesGrid from "./CompanyTemplatesGrid.tsx";
import TemplateInfoDialog from "./TemplateInfoDialog.tsx";
import TemplatesMenu from "./TemplatesMenu.tsx";

interface Props {
  templates: ReportTemplateDto[];
  isReportCreating?: boolean;
  hideCreateBlankReport?: boolean;
  createBlankReport?: () => void;
  onClose: () => void;
  onQuickUse: (template: ReportTemplate) => void;
}

export default function CompanyTemplatesDialog({
  templates,
  isReportCreating,
  hideCreateBlankReport,
  createBlankReport,
  onClose,
  onQuickUse,
}: Props) {
  const [selectedField, setSelectedField] = useState<{
    fieldValue?: string;
    fieldName?: string;
  }>();
  const [searchText, setSearchText] = useState("");
  const [templateInfo, setTemplateInfo] = useState<ReportTemplate | undefined>();

  const handleTemplateNameClick = useCallback((template: ReportTemplate) => {
    setTemplateInfo(template);
  }, []);

  const filteredTemplates = useMemo(() => {
    return templates
      .filter(
        (t) => t.name.toLowerCase().indexOf(searchText) > -1 || t.groupName.toLowerCase().indexOf(searchText) > -1
      )
      .filter((rep) => filterPredicate(rep, selectedField?.fieldName, selectedField?.fieldValue));
  }, [searchText, selectedField?.fieldName, selectedField?.fieldValue, templates]);

  return (
    <PromptDialog open sx={{ width: "100%", height: "100%", maxHeight: 800, maxWidth: 1300, p: 0 }}>
      <PromptDialogHeader text="Report Templates" onClose={onClose} />
      <Divider orientation="horizontal" flexItem />
      <Grid container sx={{ flex: 1, height: "100%", width: "100%", flexWrap: "nowrap" }}>
        <Grid sx={(theme) => ({ width: "100%", maxWidth: theme.spacing(35), gap: 2.5, display: "flex" })}>
          <Stack sx={{ flex: 1 }}>
            <TemplatesMenu
              displayingTemplates={templates}
              allTemplatesList={templates}
              onSelect={(item) => setSelectedField({ fieldValue: item?.value, fieldName: item?.fieldName })}
              sx={{ py: 2.5, px: 2, flex: 1 }}
              showNewLabel={true}
            />
            {!hideCreateBlankReport && (
              <Box sx={(theme) => ({ display: "flex", p: 2, borderTop: `1px solid ${theme.palette.divider}` })}>
                <Button
                  variant="contained"
                  sx={{ px: 1.25, flex: 1 }}
                  startIcon={<AddRoundedIcon />}
                  onClick={() => createBlankReport?.()}
                  disabled={isReportCreating}
                  loading={isReportCreating}
                >
                  Create Blank Report
                </Button>
              </Box>
            )}
          </Stack>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Stack spacing={2.5} useFlexGap sx={{ width: "100%", pt: 3, pr: 3, pb: 2, pl: 2.5 }}>
          <Grid container sx={{ justifyContent: "space-between", width: "100%" }}>
            <Typography variant="subtitle2">{`${filteredTemplates.length} record${
              filteredTemplates.length !== 1 ? "(s)" : ""
            }`}</Typography>
            <SearchField onSearch={(text) => setSearchText(text.toLowerCase())} debounceTimeMs={300} />
          </Grid>
          <CompanyTemplatesGrid
            templates={filteredTemplates}
            isReportCreating={isReportCreating}
            onTemplateNameClick={handleTemplateNameClick}
            onQuickUse={onQuickUse}
          />
        </Stack>
      </Grid>
      {templateInfo && (
        <TemplateInfoDialog
          template={templateInfo}
          onClose={() => setTemplateInfo(undefined)}
          onUseTemplate={(t) => {
            setTemplateInfo(undefined);
            onQuickUse(t);
          }}
        />
      )}
    </PromptDialog>
  );
}

function filterPredicate(template: ReportTemplateDto, fieldName?: string, fieldValue?: string) {
  switch (fieldName) {
    case "Fund Types":
      return template.fundTypes.includes(fieldValue as FundType);
    case "Groups":
      return template.groupName === fieldValue;
    default:
      return true;
  }
}
